import axios from "axios";

const trimTrailingSlashes = (value) => value.replace(/\/+$/, "");

const getApiUrl = () => {
  const configuredUrl = (process.env.REACT_APP_API_URL || "").trim();
  return configuredUrl ? trimTrailingSlashes(configuredUrl) : "/api";
};

export const API_BASE_URL = getApiUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: false,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    const apiKey = localStorage.getItem("api_key");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (apiKey) {
      config.headers["X-API-Key"] = apiKey;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes("/auth/login");

      if (!isLoginRequest) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_data");
        localStorage.removeItem("api_key");
        localStorage.removeItem("current_client");

        if (!window.location.pathname.includes("/login")) {
          window.location.assign("/login");
        }
      }
    }

    return Promise.reject(error);
  },
);

class ApiService {
  async login(username, password) {
    try {
      const response = await apiClient.post("/auth/login", {
        username,
        password,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async logout() {
    try {
      const response = await apiClient.post("/auth/logout");
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProfile() {
    try {
      const response = await apiClient.get("/auth/profile");
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAnalytics(endpoint, params = {}) {
    try {
      const response = await apiClient.get(`/analytics/${endpoint}`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  getOverview(params) {
    return this.getAnalytics("overview", params);
  }

  getDailyTrend(params) {
    return this.getAnalytics("daily-trend", params);
  }

  getTopProducts(params) {
    return this.getAnalytics("top-products", params);
  }

  getSellerPerformance(params) {
    return this.getAnalytics("seller-performance", params);
  }

  getProductPerformance(params) {
    return this.getAnalytics("product-performance", params);
  }

  getShopRevenue(params) {
    return this.getAnalytics("shop-revenue", params);
  }

  async getInvoiceDetails(invoiceId) {
    try {
      const response = await apiClient.get(
        `/analytics/invoice/${encodeURIComponent(invoiceId)}`,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  getMonthComparison(params) {
    return this.getAnalytics("month-comparison", params);
  }
  getShopComparison(params) {
    return this.getAnalytics("shop-comparison", params);
  }
  getAdvancedAnalytics(params) {
    return this.getAnalytics("advanced", params);
  }

  async getShops() {
    try {
      const response = await apiClient.get("/analytics/shops");
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async testCORS() {
    try {
      const response = await apiClient.get("/test-cors.php");
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  getFraudDetection(params) {
    return this.getAnalytics("fraud-detection", params);
  }

  async getStatus() {
    try {
      const response = await apiClient.get("/status.php");
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    let message = "An unexpected error occurred.";

    if (error.response) {
      message =
        error.response.data?.message ||
        error.response.data?.error ||
        `Server request failed (${error.response.status}).`;
    } else if (error.request) {
      message = `Unable to reach the API at ${API_BASE_URL}. Check the server, DNS, SSL, and CORS settings.`;
    } else if (error.message) {
      message = error.message;
    }

    const normalizedError = new Error(message);
    normalizedError.status = error.response?.status;
    normalizedError.response = error.response;
    normalizedError.cause = error;
    return normalizedError;
  }
}

const api = new ApiService();
export default api;
